import { useEffect, useState } from "react";

import {
  fetchRegionalMeasurement,
  fetchRegionalMeasurementSignals,
} from "../../utils/backend/pdxBackendClient";

const RESULT_POLL_MS = 4000;
const DEFAULT_SIGNAL_ALGORITHM = "mpm";

function isNotFound(error) {
  return (
    error?.status === 404 ||
    error?.body?.error?.code === "MEASUREMENT_NOT_FOUND"
  );
}

function buildSignalProcessingFieldName(signalAlgorithm = DEFAULT_SIGNAL_ALGORITHM) {
  const normalizedAlgorithm =
    typeof signalAlgorithm === "string" && signalAlgorithm.trim().length > 0
      ? signalAlgorithm.trim().toLowerCase()
      : DEFAULT_SIGNAL_ALGORITHM;

  return `${normalizedAlgorithm}-signal-processed`;
}

function stripLegacySignalProcessing(measurement) {
  if (!measurement || typeof measurement !== "object") {
    return measurement;
  }

  const sanitizedMeasurement = { ...measurement };

  delete sanitizedMeasurement["mpm-signal-processed"];
  delete sanitizedMeasurement["pca-signal-processed"];
  delete sanitizedMeasurement.mpmSignalProcessed;
  delete sanitizedMeasurement.mpm_signal_processed;
  delete sanitizedMeasurement.pcaSignalProcessed;
  delete sanitizedMeasurement.pca_signal_processed;

  return sanitizedMeasurement;
}

function mergeSignalProcessing(measurement, signalResponse) {
  const sanitizedMeasurement = stripLegacySignalProcessing(measurement);

  if (!sanitizedMeasurement || typeof sanitizedMeasurement !== "object") {
    return sanitizedMeasurement;
  }

  const signalProcessing =
    signalResponse?.signalProcessing &&
    typeof signalResponse.signalProcessing === "object"
      ? signalResponse.signalProcessing
      : null;

  if (!signalProcessing) {
    return sanitizedMeasurement;
  }

  return {
    ...sanitizedMeasurement,
    [buildSignalProcessingFieldName(signalResponse?.signalAlgorithm)]:
      signalProcessing,
  };
}

function readSignalArray(signalProcessing, ...keys) {
  for (const key of keys) {
    if (Array.isArray(signalProcessing?.[key])) {
      return signalProcessing[key];
    }
  }

  return [];
}

function summarizeSignalProcessing(signalProcessing) {
  if (!signalProcessing || typeof signalProcessing !== "object") {
    return null;
  }

  const values = readSignalArray(
    signalProcessing,
    "detrendedTrace",
    "detrendedSignal",
    "detrended_signal",
  );
  const timeCandidates = [
    signalProcessing.resampledTimesSeconds,
    signalProcessing.timesSeconds,
    signalProcessing.timeSeconds,
    signalProcessing.time_seconds,
  ];
  const times =
    timeCandidates.find(
      (candidate) => Array.isArray(candidate) && candidate.length === values.length,
    ) || [];
  const samplingRateHz =
    signalProcessing?.samplingRateHz ?? signalProcessing?.sampling_rate_hz ?? null;
  const firstTime = Number.isFinite(times[0]) ? times[0] : null;
  const lastTime =
    Number.isFinite(times[times.length - 1]) ? times[times.length - 1] : null;

  return {
    valueCount: values.length,
    timeCount: times.length,
    samplingRateHz,
    durationFromTimesSeconds:
      firstTime != null && lastTime != null ? lastTime - firstTime : null,
    estimatedDurationFromSamplesSeconds:
      Number.isFinite(samplingRateHz) && samplingRateHz > 0 && values.length > 1
        ? (values.length - 1) / samplingRateHz
        : null,
  };
}

/**
 * Polls a single measurement result document through measurement-service and
 * hydrates its signal traces through results-service.
 *
 * Measurement data comes from `/v1/app/measurements/:variant/:measurementId`.
 */
export function useMeasurementResultDoc(uid, variant, docId) {
  const [data, setData] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(!!uid && !!variant && !!docId);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState({
    fromCache: false,
    hasPendingWrites: false,
  });

  useEffect(() => {
    if (!uid || !variant || !docId) {
      setData(null);
      setExists(false);
      setLoading(false);
      setError(null);
      setMetadata({ fromCache: false, hasPendingWrites: false });
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;

    const poll = async (isInitial = false) => {
      if (isInitial) {
        setLoading(true);
      }
      setError(null);
      setMetadata({ fromCache: false, hasPendingWrites: false });

      try {
        const response = await fetchRegionalMeasurement({
          variant,
          measurementId: docId,
        });

        if (cancelled) return;

        const measurement = response?.measurement || null;
        let mergedMeasurement = stripLegacySignalProcessing(measurement);
        if (process.env.NODE_ENV !== "test") {
          console.log("[result signal] measurement fetched", {
            variant,
            docId,
            hasMeasurement: Boolean(measurement),
            status: measurement?.status || null,
            upload: measurement?.upload || null,
            measurementId:
              measurement?.measurementId ||
              measurement?.recordingId ||
              measurement?.docId ||
              docId,
          });
        }

        if (measurement) {
          try {
            const signalResponse = await fetchRegionalMeasurementSignals({
              variant,
              measurementId: docId,
              signalAlgorithm: DEFAULT_SIGNAL_ALGORITHM,
            });

            if (cancelled) return;
            if (process.env.NODE_ENV !== "test") {
              console.log("[result signal] signal payload fetched", {
                variant,
                docId,
                signalAlgorithm:
                  signalResponse?.signalAlgorithm || DEFAULT_SIGNAL_ALGORITHM,
                signalSummary: summarizeSignalProcessing(
                  signalResponse?.signalProcessing,
                ),
              });
            }

            mergedMeasurement = mergeSignalProcessing(
              measurement,
              signalResponse,
            );
          } catch (signalError) {
            if (cancelled) return;
            if (process.env.NODE_ENV !== "test") {
              console.warn("[result signal] signal payload fetch failed", {
                variant,
                docId,
                errorMessage: signalError?.message || String(signalError),
              });
            }
          }
        }

        setData(mergedMeasurement);
        setExists(Boolean(measurement));
        setLoading(false);
      } catch (fetchError) {
        if (cancelled) return;

        if (isNotFound(fetchError)) {
          setData(null);
          setExists(false);
          setError(null);
        } else {
          setError(fetchError);
        }
        setLoading(false);
      }

      if (!cancelled) {
        timeoutId = setTimeout(() => poll(false), RESULT_POLL_MS);
      }
    };

    poll(true);

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [docId, uid, variant]);

  return { data, exists, loading, error, metadata };
}
